import { useState } from "react";
import { toast } from "sonner";

import api, { formatApiError } from "../../lib/api";
import { PrimaryButton, SecondaryButton } from "../crm/Form";

/**
 * Phase 8 — Send-reminder button on the OwnerAcknowledgmentPanel.
 *
 * Pre-ack label: "Send reminder"
 * Post-ack label: "Resend share link"
 *
 * The dedupe is server-side (1-per-day-per-report) so repeat clicks return
 * 429 with an explanatory toast.
 */
export default function SendReminderButton({ reportId, hasActiveAck, onSent }) {
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        `/diagnostic-reports/${reportId}/request-acknowledgment`,
        {}
      );
      const status = r.data?.notification?.status;
      if (status === "stubbed_no_provider") {
        toast.success(
          "Reminder queued (stub mode — set RESEND_API_KEY to send for real)"
        );
      } else if (status === "sent") {
        toast.success("Reminder email sent.");
      } else {
        toast.message("Reminder queued.", { description: status });
      }
      onSent?.(r.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        toast.error(
          (detail && detail.message) ||
            "A reminder for this report was already sent today."
        );
      } else if (status === 400 && detail?.error === "no_customer_email") {
        toast.error(
          detail.message ||
            "Customer has no email on file. Add one to send a reminder."
        );
      } else if (status === 409) {
        toast.error("Report must be finalized first.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const Btn = hasActiveAck ? SecondaryButton : PrimaryButton;
  return (
    <Btn
      type="button"
      onClick={send}
      disabled={busy}
      data-testid="send-reminder-btn"
    >
      {busy
        ? "Sending…"
        : hasActiveAck
        ? "Resend share link"
        : "Send reminder"}
    </Btn>
  );
}
